import { Trans, t } from '@lingui/macro';
import { CheckCircle2, Trash2, Loader2, Key } from "lucide-react";
import { useEffect, useState } from 'react';
import { Fire } from '../../../services/FireClient';
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";

const WSOpenAIKey: React.FC<{ workspaceId: string }> = ({ workspaceId }) => {
  const [inProgress, setInProgress] = useState(false);
  const [existingKeyLast4, setExistingKeyLast4] = useState('');
  const [newKey, setNewKey] = useState('');
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  useEffect(() => {
    loadExistingKey();
  }, [workspaceId]);

  function loadExistingKey() {
    setInProgress(true);
    Fire.bot.getOpenAIKeyLast4(workspaceId).then(str => {
      setExistingKeyLast4(str);
      setInProgress(false);
    });
  }

  async function deleteKey() {
    setInProgress(true);
    try {
      await Fire.bot.deleteOpenAIKey(workspaceId);
      setExistingKeyLast4('');
    } catch (error) {
      console.error(error);
    } finally {
      setInProgress(false);
      setShowDeleteAlert(false);
    }
  }

  async function saveKey() {
    if (!newKey) return;
    setInProgress(true);
    try {
      await Fire.bot.setOpenAIKey(workspaceId, newKey);
      await loadExistingKey();
      setNewKey('');
    } catch (error) {
      console.error(error);
      setInProgress(false);
    }
  }

  if (inProgress && !existingKeyLast4 && !newKey) { // Initial loading
    return <div className="flex justify-center p-4"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-4">
      {inProgress && (
        <div className="flex justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
        </div>
      )}

      {existingKeyLast4 ? (
        <div className="flex items-center justify-between p-3 border rounded-md bg-green-50/50 border-green-100">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-900">
              <Trans>OpenAI Key is set</Trans> (sk-...{existingKeyLast4})
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowDeleteAlert(true)}
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-sm font-medium"><Trans>Insert OpenAI Key to enable Soribots</Trans></Label>
            <div className="flex gap-2">
              <Input
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder={t`Get your key from https://platform.openai.com/api-keys`}
                type="password"
                className="flex-1"
                autoComplete="off"
              />
              <Button onClick={saveKey} disabled={!newKey || inProgress}>
                {inProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trans>Save Key</Trans>}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              <Trans>Your key is stored securely and used only for AI features.</Trans>
            </p>
          </div>
        </div>
      )}

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><Trans>Delete OpenAI Key</Trans></AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>Are you sure you want to delete the OpenAI Key?</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction onClick={deleteKey} className="bg-red-500 hover:bg-red-600">
              <Trans>Delete</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WSOpenAIKey;


